/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import {
    CustomersService as ICustomersService,
    GlobalConfigurationService as IGlobalConfigurationService,
    ModelFieldValueTranslationsService as IModelFieldValueTranslationsService,
    PlansService as IPlansService,
    PricesService as IPricesService,
    ProductsService as IProductsService,
    Service,
    SubscriptionsService as ISubscriptions,
    RolesService as IRolesService,
    ConsoleUsersService as IConsoleUsersService,
    AccountService as IAccountService,
    ProfileService as IProfileService,
    Users as IUsers
} from "App/Contracts";

import { Coupon, Invoice, PriceTarget, Role } from "App/Models";

import { DataStore } from "App/IOC";

import BaseService from "App/Externals/Services/Base";
import CustomersService from "App/Externals/Services/Customers";
import ConsoleUsersService from "App/Externals/Services/ConsoleUsers";
import AccountService from "App/Externals/Services/Account";
import ProfileService from "App/Externals/Services/Profile";
import ModelFieldValueTranslationsService from "App/Externals/Services/ModelFieldValueTranslations";
import PlansService from "App/Externals/Services/Plans";
import PricesService from "App/Externals/Services/Prices";
import PriceVersionsService from "App/Externals/Services/PriceVersions";
import PriceAuditService from "App/Externals/Services/PriceAudit";
import SubscriptionAuditService from "App/Externals/Services/SubscriptionAudit";
import ProductsService from "App/Externals/Services/Products";
import SubscriptionsService from "App/Externals/Services/Subscriptions";
import UsersService from "App/Externals/Services/Users";
import { Services } from "App/Externals/IOC";
import GlobalConfiguration from "App/Externals/Services/GlobalConfiguration";
import AdsPublicationService from "App/Externals/Services/AdsPublication";
import SetupGatewayService from "App/Externals/Services/SetupGateway";
import RevenueService from "App/Externals/Services/Revenue";
import AgentKbService from "App/Externals/Services/AgentKb";
import ApiKeysService from "App/Externals/Services/ApiKeys";
import NewslettersService from "App/Externals/Services/Newsletters";
import DesignCategoriesService from "App/Externals/Services/DesignCategories";

const service = "Internal";

const Customers: ICustomersService = new CustomersService(
    Services.Get( `${service}/Customers` ),
    Services.Get( `${service}/Customers/Invoices` ),
    Services.Get( `${service}/Customers/GroupUsers` ),
    Services.Get( `${service}/Customers/GroupOverview` ),
    Services.Get( `${service}/Users/List` ),
    Services.Get( `${service}/Customers/PaymentMethod` ),
    Services.Get( `${service}/Customers/ByGroups` )
);
const Products: IProductsService = new ProductsService( Services.Get( `${service}/Products` ) );

const Plans: IPlansService = new PlansService(
    Services.Get( `${service}/Plans` ),
    Services.Get( `${service}/Plans/Duplication` )
);

const Prices: IPricesService = new PricesService( Services.Get( `${service}/Prices` ) );

const PriceVersions = new PriceVersionsService(
    Services.Get( `${service}/PriceVersions` ),
    Services.Get( `${service}/Prices/Publish` ),
    Services.Get( `${service}/Prices/Revise` )
);

const PriceAudit = new PriceAuditService( Services.Get( `${service}/PriceAudit` ) );
const SubscriptionAudit =
    new SubscriptionAuditService( Services.Get( `${service}/SubscriptionAudit` ) );
const Users: IUsers = new UsersService(
    Services.Get( "Authentication" ),
    Services.Get( `${service}/Users/Current` ),
    DataStore
);

const Subscriptions: ISubscriptions = new SubscriptionsService(
    Services.Get( `${service}/Subscriptions` ),
    Services.Get( `${service}/Subscriptions/GroupTargetLines` ),
    Services.Get( `${service}/Subscriptions/Unsubscribe` ),
    Services.Get( `${service}/Subscriptions/ChangeRecurrence` ),
    Services.Get( `${service}/Subscriptions/MigrateAllToActiveVersions` ),
    Services.Get( `${service}/Subscriptions/OutdatedPlanIds` ),
    Services.Get( `${service}/SubscriptionMigration` ),
    Services.Get( `${service}/Subscriptions/RetryMigration` ),
    Services.Get( `${service}/Subscriptions/MigrationCandidates` )
);

const Coupons: Service<Coupon> = new BaseService( Services.Get( `${service}/Coupons` ) );

/* Custom action endpoint: stop a coupon now (block redemptions + end active discounts). */
const CouponsStop = Services.Get( `${service}/Coupons/Stop` );

/* Read endpoint: customers who redeemed a coupon + their first subscription date. */
const CouponsUsage = Services.Get( `${service}/Coupons/Usage` );

const GlobalConfigurationService: IGlobalConfigurationService = new GlobalConfiguration(
    Services.Get( `${service}/GlobalConfiguration` )
);

const PriceTargets: Service<PriceTarget> = new BaseService(
    Services.Get( `${service}/PriceTargets` )
);

const Payments: Service<Invoice> = new BaseService( Services.Get( `${service}/Payments` ) );

const Revenue = new RevenueService( Services.Get( `${service}/Revenue` ) );

const AgentKb = new AgentKbService(
    Services.Get( `${service}/AgentKb/Exchanges` ),
    Services.Get( `${service}/AgentKb/Conversations` ),
    Services.Get( `${service}/AgentKb/Usage` )
);

const ApiKeys = new ApiKeysService( Services.Get( `${service}/ApiKeys` ) );

const Newsletters = new NewslettersService( Services.Get( `${service}/Newsletters` ) );

const DesignCategories = new DesignCategoriesService(
    Services.Get( `${service}/DesignCategories` )
);

const ModelFieldValueTranslations: IModelFieldValueTranslationsService =
    new ModelFieldValueTranslationsService( Services.Get(
        `${service}/ModelFieldValueTranslations` )
    );

/* Console roles: standard CRUD. */
const Roles: IRolesService = new BaseService<Role>( Services.Get( `${service}/Roles` ) );

/* Console staff users: list/detail + invitation/lifecycle action endpoints. */
const ConsoleUsers: IConsoleUsersService = new ConsoleUsersService(
    Services.Get( `${service}/ConsoleUsers` ),
    Services.Get( `${service}/ConsoleUsers/Invite` ),
    Services.Get( `${service}/ConsoleUsers/Disable` ),
    Services.Get( `${service}/ConsoleUsers/Reactivate` ),
    Services.Get( `${service}/ConsoleUsers/Reinvite` ),
    Services.Get( `${service}/ConsoleUsers/AssignRole` )
);

/* Authenticated self-service for the signed-in user (profile page): identity, password, 2FA. */
const Profile: IProfileService = new ProfileService(
    Services.Get( `${service}/Account/Profile` ),
    Services.Get( `${service}/Account/Password` ),
    Services.Get( `${service}/Account/Totp` ),
    Services.Get( `${service}/Account/Totp/Begin` ),
    Services.Get( `${service}/Account/Totp/Enable` ),
    Services.Get( `${service}/Account/Totp/Disable` ),
    Services.Get( `${service}/Account/Totp/Recovery` ),
    Services.Get( `${service}/Account/Sessions` ),
    Services.Get( `${service}/Account/Sessions/Revoke` )
);

/* Public activation endpoints (outside "/Internal/"). */
const Account: IAccountService = new AccountService(
    Services.Get( "Account/Invitation" ),
    Services.Get( "Account/Activate" ),
    Services.Get( "Account/Login" ),
    Services.Get( "Account/Totp/Confirm" ),
    Services.Get( "Account/ForgotPassword" ),
    Services.Get( "Account/ResetToken" ),
    Services.Get( "Account/ResetPassword" )
);

const AdsPublications =
    new AdsPublicationService( Services.Get( "Configuration/Gateway/GatewayFtp" ),
        Services.Get( "Configuration/Gateway/Picture" ),
        Services.Get( "Configuration/Gateway/Customer" ),
        Services.Get( "Configuration/Gateway/GatewaySerializer" )
    );

/* API-type gateways (Amanda and the other API portals). Same service shape as AdsPublications but
   create/get/update route to the /gateway-api/* endpoints. List/serializers/customers/picture stay
   shared with the FTP-bound instance above. */
const AdsPublicationsApi =
    new AdsPublicationService( Services.Get( "Configuration/Gateway/GatewayApi" ),
        Services.Get( "Configuration/Gateway/Picture" ),
        Services.Get( "Configuration/Gateway/Customer" ),
        Services.Get( "Configuration/Gateway/GatewaySerializer" )
    );

/* Per-user gateway setups (a customer user activating a portal — Amanda — with its credentials).
   create scoped by user_id, list/get/update/delete scoped by groupId. */
const SetupGateway =
    new SetupGatewayService( Services.Get( "Configuration/Gateway/SetupGateway" ) );

export {
    Coupons,
    CouponsStop,
    CouponsUsage,
    AdsPublications,
    AdsPublicationsApi,
    SetupGateway,
    Customers,
    GlobalConfigurationService,
    ModelFieldValueTranslations,
    Payments,
    Products,
    Plans,
    Prices,
    PriceVersions,
    PriceAudit,
    SubscriptionAudit,
    PriceTargets,
    Revenue,
    AgentKb,
    ApiKeys,
    Newsletters,
    DesignCategories,
    Roles,
    ConsoleUsers,
    Account,
    Profile,
    Subscriptions,
    Users
};
